import { useCallback, useState } from 'react';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import StarIcon from '@material-ui/icons/Star';
import { Approach } from '../../api/types';

export interface ApproachViewerProps {
  approaches: Approach[];
  question: string;
  onSelect?: (approachTitle: string) => void;
  disabled?: boolean;
  stackedLayout?: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  question: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
  },
  selectedCard: {
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
    boxShadow: `0 0 0 1px ${theme.palette.secondary.main}`,
  },
  recommendedCard: {
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
  },
  cardContent: {
    flex: 1,
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    fontSize: '0.95rem',
    flex: 1,
  },
  recommendedChip: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    height: 22,
    fontSize: '0.7rem',
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(1.5),
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: '0.78rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
  },
  prosLabel: {
    color: '#2e7d32',
  },
  consLabel: {
    color: '#c62828',
  },
  tradeoffsLabel: {
    color: '#666',
  },
  list: {
    padding: 0,
  },
  listItem: {
    paddingTop: 1,
    paddingBottom: 1,
    paddingLeft: 0,
  },
  listItemIcon: {
    minWidth: 28,
  },
  proIcon: {
    color: '#2e7d32',
    fontSize: '1rem',
  },
  conIcon: {
    color: '#c62828',
    fontSize: '1rem',
  },
  tradeoffIcon: {
    color: '#666',
    fontSize: '1rem',
  },
  listItemText: {
    fontSize: '0.82rem',
  },
  radio: {
    color: theme.palette.divider,
    '&.Mui-checked': {
      color: theme.palette.secondary.main,
    },
    padding: theme.spacing(0.5),
  },
  recommendedStar: {
    color: theme.palette.secondary.main,
    fontSize: '1.1rem',
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
  headerRow: {
    marginBottom: theme.spacing(1.5),
  },
}));

export const ApproachViewer = ({
  approaches,
  question,
  onSelect,
  disabled = false,
  stackedLayout = false,
}: ApproachViewerProps) => {
  const classes = useStyles();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleCardClick = useCallback(
    (index: number) => {
      if (!disabled) {
        setSelectedIndex(index);
      }
    },
    [disabled],
  );

  const handleSubmit = useCallback(() => {
    if (selectedIndex !== null && onSelect) {
      onSelect(approaches[selectedIndex].title);
    }
  }, [selectedIndex, approaches, onSelect]);

  if (!approaches || approaches.length === 0) {
    return null;
  }

  const isInteractive = !!onSelect;

  return (
    <div className={classes.root} data-testid="approach-viewer">
      <div className={classes.headerRow}>
        <Typography
          variant="subtitle1"
          className={classes.question}
          style={{ marginBottom: 0 }}
        >
          {question}
        </Typography>
      </div>
      <Grid container spacing={2}>
        {approaches.map((approach, index) => {
          const isSelected = selectedIndex === index;
          const cardClassName = [
            classes.card,
            isSelected ? classes.selectedCard : '',
            !isSelected && approach.recommended ? classes.recommendedCard : '',
          ]
            .filter(Boolean)
            .join(' ');

          const cardBody = (
            <CardContent className={classes.cardContent}>
              <div className={classes.titleRow}>
                {isInteractive && (
                  <Radio
                    checked={isSelected}
                    className={classes.radio}
                    inputProps={{
                      'aria-label': `Selecionar ${approach.title}`,
                    }}
                    disabled={disabled}
                    size="small"
                  />
                )}
                {approach.recommended && (
                  <StarIcon className={classes.recommendedStar} />
                )}
                <Typography className={classes.title}>
                  {approach.title}
                </Typography>
                {approach.recommended && (
                  <Chip
                    label="Recomendado"
                    size="small"
                    className={classes.recommendedChip}
                    data-testid="recommended-badge"
                  />
                )}
              </div>
              <Typography className={classes.description}>
                {approach.description}
              </Typography>

              {approach.pros.length > 0 && (
                <>
                  <Typography
                    className={`${classes.sectionLabel} ${classes.prosLabel}`}
                  >
                    Pros
                  </Typography>
                  <List className={classes.list} dense>
                    {approach.pros.map((pro, i) => (
                      <ListItem key={i} className={classes.listItem}>
                        <ListItemIcon className={classes.listItemIcon}>
                          <CheckCircleOutlineIcon className={classes.proIcon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={pro}
                          primaryTypographyProps={{
                            className: classes.listItemText,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {approach.cons.length > 0 && (
                <>
                  <Typography
                    className={`${classes.sectionLabel} ${classes.consLabel}`}
                  >
                    Cons
                  </Typography>
                  <List className={classes.list} dense>
                    {approach.cons.map((con, i) => (
                      <ListItem key={i} className={classes.listItem}>
                        <ListItemIcon className={classes.listItemIcon}>
                          <HighlightOffIcon className={classes.conIcon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={con}
                          primaryTypographyProps={{
                            className: classes.listItemText,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {approach.tradeoffs.length > 0 && (
                <>
                  <Typography
                    className={`${classes.sectionLabel} ${classes.tradeoffsLabel}`}
                  >
                    Trade-offs
                  </Typography>
                  <List className={classes.list} dense>
                    {approach.tradeoffs.map((tradeoff, i) => (
                      <ListItem key={i} className={classes.listItem}>
                        <ListItemIcon className={classes.listItemIcon}>
                          <SwapHorizIcon className={classes.tradeoffIcon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={tradeoff}
                          primaryTypographyProps={{
                            className: classes.listItemText,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          );

          return (
            <Grid
              item
              xs={12}
              md={stackedLayout || approaches.length === 1 ? 12 : 6}
              key={index}
            >
              <Card
                className={cardClassName}
                variant="outlined"
                data-testid={`approach-card-${index}`}
              >
                {isInteractive ? (
                  <CardActionArea
                    onClick={() => handleCardClick(index)}
                    disabled={disabled}
                    aria-label={`Selecionar abordagem ${approach.title}`}
                  >
                    {cardBody}
                  </CardActionArea>
                ) : (
                  cardBody
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {isInteractive && (
        <Button
          variant="contained"
          color="secondary"
          className={classes.submitButton}
          onClick={handleSubmit}
          disabled={disabled || selectedIndex === null}
          aria-label="Confirmar abordagem"
        >
          Confirmar abordagem
        </Button>
      )}
    </div>
  );
};
